import { TDatabaseId } from '@infotech-soe/metaschema';
import { ConnectedProps, legacy_connect as connect } from 'react-redux';

import { gridFeatures } from '../../../constants/dataset';
import {
  DatasetColumnWidthConfig,
  DatasetConfig,
} from '../../../models/user-config/DisplayModeConfig';
import { CommandName } from '../../../services/commands/CommandViewModel';
import UniqueId from '../../../services/uid/UniqueId';
import { AppState } from '../../../store/reducers/root';
import { getDatasetCommands } from '../../../store/selectors/commands';
import { getActiveCategoryId } from '../../../store/selectors/content';
import {
  getDatasetActiveRowId,
  getDatasetCategoryId,
  getDatasetCategoryOpenMode,
  getDatasetColumns,
  getDatasetConditions,
  getDatasetConditionsCount,
  getDatasetConfig,
  getDatasetEditableFields,
  getDatasetFieldsConditionsCount,
  getDatasetHasConditionsHook,
  getDatasetHideCheckbox,
  getDatasetKeyMap,
  getDatasetName,
  getDatasetRecordViewMap,
  getDatasetRowIdField,
  getDatasetRows,
  getDatasetSelectedIds,
  getDatasetTitle,
  isDatasetConfigFormShow,
  isDatasetRoot,
} from '../../../store/selectors/dataset';
import { getCurrentLocaleName } from '../../../store/selectors/intl';
import commandsActions from '../../../store/slices/commands';
import dataset, { DatasetSwitchEffect } from '../../../store/slices/dataset';
import errorActions from '../../../store/slices/error';

import DatasetGrid from './DatasetGrid';

interface IOwnProps {
  id: UniqueId;
}

const mapStateToProps = (state: AppState, { id }: IOwnProps) => {
  const commands = getDatasetCommands(state, id);
  const hideCheckbox = getDatasetHideCheckbox(state, id);
  const keyMap = getDatasetKeyMap(state, id);
  return {
    activeRowId: getDatasetActiveRowId(state, id),
    categoryId: getDatasetCategoryId(state, id),
    columns: getDatasetColumns(state, id),
    ...(commands && { commands }),
    conditions: getDatasetConditions(state, id),
    conditionsCount: getDatasetConditionsCount(state, id),
    hasConditionsHook: getDatasetHasConditionsHook(state, id),
    config: getDatasetConfig(state, id),
    datasetId: id,
    datasetName: getDatasetName(state, id),
    datasetTitle: getDatasetTitle(state, id),
    editableFields: getDatasetEditableFields(state, id),
    features: gridFeatures,
    fieldsConditionsCount: getDatasetFieldsConditionsCount(state, id),
    ...(hideCheckbox && { hideCheckbox }),
    isDatasetRoot: isDatasetRoot(state, id),
    isShowForm: isDatasetConfigFormShow(state, id),
    ...(keyMap && { keyMap }),
    locale: getCurrentLocaleName(state),
    openMode: getDatasetCategoryOpenMode(state, id),
    rowIdField: getDatasetRowIdField(state, id),
    rows: getDatasetRows(state, id),
    selectedIds: getDatasetSelectedIds(state, id),
    recordViewMap: getDatasetRecordViewMap(state, id),
    activeCategoryId: getActiveCategoryId(state),
  };
};

const mapDispatchToProps = {
  changeActiveRow: dataset.changeActiveRow,
  sort: dataset.sort,
  initiateCommand: commandsActions.initiate,
  initiateCommandsScheduled: commandsActions.initiateScheduled,
  updateConfig: dataset.updateConfig,
  closeConfigForm: dataset.closeConfigForm,
  changeSelectedIds: dataset.changeSelectedIds,
  getCommands: commandsActions.get,
  resizeColumn: dataset.resizeColumn,
  moveColumn: dataset.moveColumn,
  showErrorNotification: errorActions.showNotification,
};

const mergeProps = (
  sP: ReturnType<typeof mapStateToProps>,
  {
    changeActiveRow,
    sort,
    initiateCommand,
    initiateCommandsScheduled,
    updateConfig,
    closeConfigForm,
    changeSelectedIds,
    getCommands,
    resizeColumn,
    moveColumn,
    showErrorNotification,
  }: typeof mapDispatchToProps,
  { id: datasetId, ...oP }: IOwnProps
) => ({
  ...sP,
  ...oP,
  onActiveChange: (rowId: TDatabaseId) => {
    changeActiveRow({
      datasetId,
      rowId,
      effect: DatasetSwitchEffect.NOTHING,
    });
  },
  onSortChange: (field: string, multiSort?: boolean) => {
    sort({ datasetId, field, multiSort });
  },
  onInitiateCommand: (command: CommandName) => {
    initiateCommand({ datasetId, command });
  },
  onInitiateCommandsScheduled: (pending: CommandName[]) => {
    initiateCommandsScheduled({ datasetId, pending });
  },
  onUpdateConfig: (config: DatasetConfig, needToUpdate?: boolean) => {
    updateConfig({ datasetId, config, needToUpdate });
  },
  onCloseForm: () => {
    closeConfigForm({ datasetId });
  },
  onChangeSelectedIds: (selectedIds: TDatabaseId[]) => {
    changeSelectedIds({ datasetId, selectedIds });
    getCommands({ datasetId });
  },
  onResizeColumn: (config: DatasetColumnWidthConfig) => {
    resizeColumn({ datasetId, config });
  },
  onMoveColumn: (order: string[]) => {
    moveColumn({ datasetId, order });
  },
  onError: (e: Error) => {
    showErrorNotification({
      name: e.name,
      id: null,
      code: null,
      message: e.message,
      stack: e.stack || null,
      info: null,
      logout: false,
    });
  },
});

const connector = connect(mapStateToProps, mapDispatchToProps, mergeProps);

export type DatasetGridContainerProps = ConnectedProps<typeof connector>;

export default connector(DatasetGrid);
